import { Promise } from "bluebird";
import { keyBy, forEach, cloneDeep } from "lodash";

import store from "@/store";
import { getLabel } from "@/lib/componentUtil";

async function resolveEntityList(
  entityListBase,
  entityName,
  resolvedSuffix = "Resolved"
) {
  const entityList = cloneDeep(entityListBase);
  if (!Array.isArray(entityList) || !entityName) {
    return;
  }

  const resolvedEntityList = entityList.map(entity =>
    resolveEntity(entity, entityName, resolvedSuffix)
  );

  const result = await Promise.all(resolvedEntityList);

  return result;
}

async function resolveEntity(entity, entityName, resolvedSuffix = "Resolved") {
  if (!entity || !entityName) {
    return;
  }
  await store.dispatch("attribute/list", { reload: false });
  const attributeList = store.getters["attribute/filter"]({
    entityName: entityName,
  });
  const attributeMapByName = keyBy(attributeList, "name");

  let fkPromiseMap = {};

  forEach(entity, (value, attributeName) => {
    const attribute = attributeMapByName[attributeName];

    if (!value || !attribute || !attribute.fkExists || !attribute.fkName) {
      return;
    }

    if (attribute.type !== "array") {
      fkPromiseMap[`${attributeName}${resolvedSuffix}`] = resolveFk(
        value,
        attribute
      );
      return;
    }

    fkPromiseMap[`${attributeName}${resolvedSuffix}`] = [];

    if (!Array.isArray(value)) {
      return;
    }

    const valueFiltered = value.filter(
      singleValue => singleValue !== null && singleValue !== undefined
    );

    fkPromiseMap[`${attributeName}${resolvedSuffix}`] = Promise.all(
      valueFiltered.map(async value => resolveFk(value, attribute))
    );
  });

  const resolved = await Promise.props(fkPromiseMap);
  return Object.assign(entity, resolved);
}

export async function resolveFk(fk, attribute) {
  if (!fk || !attribute || !attribute.fkExists) {
    return fk;
  }

  // If fk is an array then it is an array of fks and we need to resolve each one
  if (Array.isArray(fk)) {
    return Promise.all(fk.map(async value => resolveFk(value, attribute)));
  }

  const { fkName, fkType, fkTypeValue: fkTypeValueBase } = attribute;
  if (!fkName || !store.state[fkName]) {
    return;
  }

  await Promise.all([
    store.dispatch(`${fkName}/list`, { reload: false }),
    store.dispatch(`attribute/list`, { reload: false }),
    store.dispatch(`entity/list`, { reload: false }),
  ]);

  const { readableAttribute } = store.getters["entity/find"]({
    name: fkName,
  });

  if (!fkType || fkTypeValueBase === null) {
    let resolvedEntity = store.getters[`${fkName}/getById`](fk);

    if (resolvedEntity) {
      resolvedEntity.readableText = resolvedEntity[readableAttribute];
      if (fkName === "address") {
        resolvedEntity.readableText = getAddressReadableText(resolvedEntity);
      }
    }

    return resolvedEntity;
  }

  let fkTypeValue = fkTypeValueBase;
  const fkAttributeList = store.getters["attribute/filter"]({
    entityName: fkName,
  });

  const fkFilterAttributeMap = keyBy(fkAttributeList, "name");
  const fkFilterAttribute = fkFilterAttributeMap[fkType];
  if (
    fkFilterAttribute &&
    fkFilterAttribute.valueYes !== null &&
    fkFilterAttribute !== null
  ) {
    if (fkTypeValueBase === true) {
      fkTypeValue = fkFilterAttribute.valueYes;
    }
    if (fkTypeValueBase === false) {
      fkTypeValue = fkFilterAttribute.valueNo;
    }
  }

  let resolvedEntity = store.getters[`${fkName}/find`](
    { [fkType]: fkTypeValue },
    fk
  );

  if (resolvedEntity) {
    resolvedEntity.readableText = resolvedEntity[readableAttribute];
    if (fkName === "address") {
      resolvedEntity.readableText = getAddressReadableText(resolvedEntity);
    }
  }

  return resolvedEntity;
}

export async function resolveAttributeLabel(value, attribute) {
  let resolvedValue;
  try {
    resolvedValue = await resolveFk(value, attribute);
  } catch (e) {
    console.error(e);
    return;
  }

  const { elementType, valueNo, valueYes } = attribute;
  if (elementType === "YesNo") {
    if (value === valueYes) {
      return "Yes";
    }

    if (value === valueNo) {
      return "No";
    }

    return "-";
  }

  if (resolvedValue === undefined || resolvedValue === null) {
    return;
  }

  if (Array.isArray(resolvedValue)) {
    return resolvedValue.map(getLabel);
  }

  return getLabel(resolvedValue);
}

export async function getFkOptionList(attribute) {
  if (!attribute || !attribute.fkExists) {
    return [];
  }

  const { fkName, fkType, fkTypeValue: fkTypeValueBase } = attribute;
  if (!fkName || !store.state[fkName]) {
    return [];
  }

  await Promise.all([
    store.dispatch(`attribute/list`, { reload: false }),
    store.dispatch(`${fkName}/list`, { reload: false }),
    store.dispatch(`entity/list`, { reload: false }),
  ]);

  const { readableAttribute } = store.getters["entity/find"]({
    name: fkName,
  });

  const idName = store.getters["attribute/filter"]({
    entityName: `${fkName}`,
  }).find(attribute => attribute.primaryKey).name;

  if (!fkType || fkTypeValueBase === null) {
    return store.state[fkName].list.map(entity => {
      entity.id = entity[idName];
      entity.readableText = entity[readableAttribute];
      if (fkName === "address") {
        entity.readableText = getAddressReadableText(entity);
      }

      return entity;
    });
  }

  let fkTypeValue = fkTypeValueBase;
  const fkAttributeList = store.getters["attribute/filter"]({
    entityName: fkName,
  });
  const fkFilterAttribute = keyBy(fkAttributeList, "name")[fkType];
  if (
    fkFilterAttribute &&
    fkFilterAttribute.valueYes !== null &&
    fkFilterAttribute !== null
  ) {
    if (fkTypeValueBase === true) {
      fkTypeValue = fkFilterAttribute.valueYes;
    }
    if (fkTypeValueBase === false) {
      fkTypeValue = fkFilterAttribute.valueNo;
    }
  }

  return store.getters[`${fkName}/filter`]({
    [fkType]: fkTypeValue,
  }).map(entity => {
    entity.id = entity[idName];
    entity.readableText = entity[readableAttribute];
    if (fkName === "address") {
      entity.readableText = getAddressReadableText(entity);
    }

    return entity;
  });
}

function getAddressReadableText(entity) {
  return entity.address2
    ? `${entity.address}, ${entity.address2}, ${entity.city}, ${entity.state}`
    : `${entity.address}, ${entity.city}, ${entity.state}`;
}

export default {
  getFkOptionList,
  resolveAttributeLabel,
  resolveEntity,
  resolveEntityList,
  resolveFk,
};
